import type { ProgrammaticLandingPageConfig } from '../../types'
import { KWP_SIZE_FACTS } from '../kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[5]

export const KWP_5_INSTALACION_FOTOVOLTAICA: ProgrammaticLandingPageConfig = {
  slug: 'instalacion-fotovoltaica-5-kwp-precio',
  lang: 'es',
  topicId: 'kwp-kosten-5',
  title: 'Instalación fotovoltaica de 5 kWp: precio y valores orientativos',
  metaDescription:
    '¿Cuánto cuesta una instalación fotovoltaica de 5 kWp? Superficie de tejado, número de ' +
    'paneles, desglose de costes y comparación gratuita para hogares pequeños.',
  keywords: [
    'instalación fotovoltaica 5 kWp precio',
    'placas solares 5 kWp coste',
    'cuánto cuesta una instalación de 5 kWp',
    '5 kWp instalación solar hogar',
  ],
  eyebrow: 'Tamaño de instalación: 5 kWp',
  h1: 'Instalación fotovoltaica de 5 kWp: precio y valores orientativos',
  intro:
    'Una instalación de 5 kWp es el tamaño típico de entrada para hogares pequeños sin bomba de ' +
    'calor ni coche eléctrico. Aquí encontrará valores orientativos sobre superficie de tejado y ' +
    'número de paneles, un desglose de costes y justo debajo una calculadora con el precio medio ' +
    'para 5 kWp ya calculado a partir de presupuestos reales de empresas instaladoras.',
  calculatorPrefill: { kind: 'kwp', kwp: 5 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: '¿Para quién es adecuada una instalación de 5 kWp?' },
    {
      type: 'paragraph',
      text:
        'Valor orientativo: 5 kWp suele encajar con un hogar de 2-3 personas con un consumo base ' +
        'de unos 3.500-4.000 kWh al año, sin bomba de calor ni coche eléctrico. Si más adelante se ' +
        'añade un consumidor adicional, conviene valorar de entrada un tamaño mayor como 8 o 10 ' +
        'kWp en lugar de ampliar la instalación después.',
    },
    { type: 'heading', text: 'Tejado y paneles: lo que necesita una instalación de 5 kWp' },
    {
      type: 'paragraph',
      text:
        `Valor orientativo: unos ${FACTS.roofAreaM2} m² de tejado y aproximadamente ` +
        `${FACTS.moduleCount} paneles con módulos habituales de ~440 Wp. La producción anual, con ` +
        `orientación sur y sin sombras, ronda los ${FACTS.annualYieldKwh.toLocaleString('es-ES')} kWh. ` +
        'Un cálculo exacto para su tejado lo ofrece la calculadora de dimensionamiento.',
    },
    { type: 'heading', text: 'Componentes del coste de una instalación fotovoltaica' },
    {
      type: 'list',
      items: [
        'Paneles: el mayor coste individual, normalmente en torno a un tercio del total',
        'Inversor: convierte la corriente continua generada en corriente alterna utilizable',
        'Montaje/mano de obra: fijación en el tejado, cableado',
        'Andamiaje: depende de la altura y accesibilidad del tejado',
        'Instalación eléctrica: adaptación del cuadro eléctrico, conexión a la red de la vivienda',
        'Tramitación: registro ante la distribuidora y organismos correspondientes',
      ],
    },
    {
      type: 'paragraph',
      text:
        'En instalaciones pequeñas como 5 kWp, la tramitación y el andamiaje básico representan ' +
        'proporcionalmente una parte mayor del coste total que en instalaciones más grandes, ya que ' +
        'estos costes no crecen de forma proporcional al tamaño. Por eso el precio por kWp suele ' +
        'ser algo más alto en 5 kWp que en 10 o 15 kWp.',
    },
    { type: 'heading', text: 'Amortización y autoconsumo' },
    {
      type: 'paragraph',
      text:
        'Sin batería, el autoconsumo de una instalación de 5 kWp suele rondar el 25-35 %, y con una ' +
        'batería bien dimensionada puede ser bastante más alto. La rapidez de la amortización ' +
        'depende de su perfil de consumo, la retribución por excedentes vigente y posibles ayudas. ' +
        'Un cálculo exacto para su tejado lo ofrece la calculadora de dimensionamiento.',
    },
  ],
  faq: [
    {
      q: '¿Es suficiente una instalación de 5 kWp para una vivienda unifamiliar?',
      a: 'Para hogares pequeños sin bomba de calor ni coche eléctrico, normalmente sí. Con un consumo mayor o previsiblemente creciente, suele ser mejor optar directamente por un tamaño mayor como 8 o 10 kWp en lugar de ampliar después.',
    },
    {
      q: '¿Merece la pena una batería con 5 kWp?',
      a: 'Sí, aunque dimensionada más pequeña que en instalaciones mayores. Que compense depende sobre todo de cuánta electricidad consuma durante el día - las empresas instaladoras pueden valorarlo según su perfil de consumo.',
    },
    {
      q: '¿Cuántos paneles necesito para 5 kWp?',
      a: `Valor orientativo: unos ${FACTS.moduleCount} paneles con módulos habituales de ~440 Wp, algo más o menos según el modelo exacto elegido.`,
    },
  ],
  primaryCta: { label: 'Solicitar presupuesto gratis ahora', action: 'customer' },
  secondaryCta: {
    label: 'Coste de una instalación fotovoltaica: guía completa',
    action: 'link',
    href: '/coste-instalacion-fotovoltaica',
  },
  relatedLinks: [
    { label: 'Instalación fotovoltaica de 8 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-8-kwp-precio' },
    { label: 'Instalación fotovoltaica de 10 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-10-kwp-precio' },
    { label: 'Instalación fotovoltaica de 12 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-12-kwp-precio' },
    { label: 'Cómo funciona la comparación de presupuestos', href: '/comparar-presupuestos-fotovoltaicos' },
  ],
}
